const OMIT = Symbol('omit-empty-string')

const isSecretField = (key?: string) =>
  Boolean(key && /(password|token|secret)/i.test(key))

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const normalizeValue = (
  value: unknown,
  key?: string
): unknown | typeof OMIT => {
  if (typeof value === 'string') {
    if (isSecretField(key)) return value

    const trimmed = value.trim()
    return trimmed || OMIT
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeValue(item))
      .filter((item) => item !== OMIT)
  }

  if (
    typeof URLSearchParams !== 'undefined' &&
    value instanceof URLSearchParams
  ) {
    const result = new URLSearchParams()
    value.forEach((entryValue, entryKey) => {
      const normalized = normalizeValue(entryValue, entryKey)
      if (normalized !== OMIT) result.append(entryKey, String(normalized))
    })
    return result
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const result = new FormData()
    value.forEach((entryValue, entryKey) => {
      const normalized = normalizeValue(entryValue, entryKey)
      if (normalized !== OMIT) {
        result.append(entryKey, normalized as string | Blob)
      }
    })
    return result
  }

  if (!isPlainObject(value)) return value

  return Object.entries(value).reduce<Record<string, unknown>>(
    (result, [entryKey, entryValue]) => {
      const normalized = normalizeValue(entryValue, entryKey)
      if (normalized !== OMIT) result[entryKey] = normalized
      return result
    },
    {}
  )
}

/** Trims outgoing text fields and omits values that contain only whitespace. */
export const trimRequestData = <T>(value: T): T => {
  const normalized = normalizeValue(value)
  return (normalized === OMIT ? undefined : normalized) as T
}
